import React from "react";

type Product = {
  title: string;
  description: string;
  image: string;
  url?: string;
};

type ProductsSectionProps = { products: Product[] };

const ProductsSection: React.FC<ProductsSectionProps> = ({ products }) => (
  <section className="bg-[#F5F7FA] pt-5 md:py-16 md:pb-[112px]">
    {/* 80 px gutters on ≥501 px, none on very small screens */}
    <div className="w-full px-[80px] max-[500px]:px-0 mx-auto">
      {/* heading */}
      <header className="mb-[60px] max-[500px]:mb-8 max-[500px]:px-4">
        <p className="font-inter font-semibold text-[22px] leading-[150%] tracking-[-0.005em] text-[#46C1BF] mb-2 max-[500px]:text-[16px]">
          Our Products //
        </p>
        <h2 className="font-utility font-medium text-[54px] leading-[120%] tracking-[-0.01em] capitalize text-[#011621] mb-2 max-[500px]:text-[30px] max-[500px]:leading-[123%]">
          Glass That Performs
        </h2>
        <h2 className="font-utility font-medium text-[54px] leading-[120%] tracking-[-0.01em] capitalize text-[#011621] max-[500px]:text-[30px] max-[500px]:leading-[123%]">
          Designs That Inspire
        </h2>
      </header>

      {/* product cards */}
      <div
        className="
          grid 
          md:grid-cols-2                /* two columns ≥ md, one column below */
          gap-x-0 gap-y-0               /* no horizontal gap */
          max-[500px]:gap-y-[16px]      /* small vertical gap on mobile */
        "
      >
        {products.map((product, idx) => (
          <a
            key={idx}
            href={product.url ?? "#"}
            className="
              group relative overflow-hidden cursor-pointer
              w-full aspect-[640/455]          /* dynamic height */
              flex items-center justify-center
             
              max-[500px]:rounded-none
            "
          >
            {/* background image */}
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
              style={{ backgroundImage: `url(${product.image})` }}
            />
            {/* dark gradient overlay */}
            <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            {/* text content */}
            <div className="relative z-20 flex flex-col items-center justify-center text-center w-full h-full px-8 max-[500px]:px-4">
              <h3 className="font-utility text-4xl font-medium mb-4 text-white max-[500px]:text-[24px] max-[500px]:mb-2">
                {product.title}
              </h3>
              <p className="font-inter text-[20px] max-w-[90%] mx-auto mb-4 text-white !font-normal max-[500px]:text-[14px] max-[500px]:leading-[150%]">
                {product.description}
              </p>

              {/* CTA line animation */}
              <span className="relative inline-block text-white text-lg font-medium focus:outline-none max-[500px]:text-[16px]">
                <span className="relative inline-block group">
                  <span className="font-inter relative z-10">Explore&nbsp;More</span>
                  {/* disappearing line */}
                  <span className="absolute left-0 bottom-0 h-[2px] bg-white w-full origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-100" />
                  {/* appearing line */}
                  <span className="absolute left-0 bottom-0 h-[2px] bg-white w-full origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 delay-200" />
                </span>
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

export default ProductsSection;
